import React, { useState, useEffect } from 'react'
import { Clock, CheckCircle, XCircle, AlertCircle, BarChart3, Eye, History } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { evaluationService } from '@/services/evaluationService'
import type { EvaluationRunSummary } from '@shared/types/evaluation'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import { PROTECTED_ROUTES } from '@/constants/routes'

interface EvaluationHistoryProps {
  testSetId: string
  onViewRun?: (runId: string) => void
}

export const EvaluationHistory: React.FC<EvaluationHistoryProps> = ({ testSetId, onViewRun }) => {
  const [history, setHistory] = useState<EvaluationRunSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await evaluationService.getEvaluationHistory(testSetId, 20)
        setHistory(data)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch evaluation history'
        setError(errorMessage)
        toast.error('Failed to load evaluation history')
      } finally {
        setLoading(false)
      }
    }

    fetchHistory()
  }, [testSetId])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'RUNNING':
        return <Clock className="h-4 w-4 text-blue-500" />
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusBadge = (status: string) => {
    const variants = {
      COMPLETED: 'default' as const,
      RUNNING: 'secondary' as const,
      FAILED: 'destructive' as const,
      CANCELLED: 'outline' as const
    }
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'outline'}>
        {status}
      </Badge>
    )
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded"></div>
          <div className="h-32 bg-muted rounded"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to Load History</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (history.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Evaluation History</h3>
            <p className="text-muted-foreground mb-4">
              No evaluations have been run for this test set yet.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Evaluation History</h3>
          <p className="text-sm text-muted-foreground">
            {history.length} evaluation run{history.length !== 1 ? 's' : ''} found
          </p>
        </div>
      </div>

      {/* History List */}
      <div className="space-y-4">
        {history.map((run) => (
          <Card key={run.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusIcon(run.status)}
                    <CardTitle className="text-base">
                      Run on {new Date(run.startedAt).toLocaleString()}
                    </CardTitle>
                    {getStatusBadge(run.status)}
                  </div>
                  <CardDescription>
                    By {run.runnerName}
                  </CardDescription>
                </div>
                
                <div className="flex gap-2">
                  <Link to={`${PROTECTED_ROUTES.EVALUATIONS}/${testSetId}/results`}>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Eye className="h-4 w-4" />
                      View Results
                    </Button>
                  </Link>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Cases</div>
                  <div className="text-lg font-semibold">
                    {run.completedCases}/{run.totalCases}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Score</div>
                  <div className="text-lg font-semibold">
                    {run.averageScore !== undefined ? run.averageScore.toFixed(1) : 'N/A'}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Pass Rate</div>
                  <div className="text-lg font-semibold">
                    {run.totalCases > 0 ? Math.round((run.passedCases / run.totalCases) * 100) : 0}%
                  </div>
                </div>
                
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Duration</div>
                  <div className="text-lg font-semibold">
                    {run.completedAt ? (
                      `${Math.round((new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()) / 1000 / 60)}m`
                    ) : (
                      run.status === 'RUNNING' ? 'Running...' : 'N/A'
                    )}
                  </div>
                </div>
              </div>
              
              {run.status === 'COMPLETED' && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Passing Score: {run.passingScore}/10
                    </span>
                    <span className="text-muted-foreground">
                      {run.passedCases} passed, {run.failedCases} failed
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}